import { createContext, useCallback, useContext } from "react";
import type { ReactNode } from "react";
import { useRoomContext } from "@livekit/components-react";
import type { CallDeviceKind, DeviceSelection } from "../lib/callDevices";
import { useCallDevices, type UseCallDevicesResult } from "./useCallDevices";
import { CallDeviceNotice } from "../components/CallDeviceNotice";

const CallDevicesContext = createContext<UseCallDevicesResult | null>(null);

/**
 * The live call's devices (GH-1859). `null` outside a call — a surface that can
 * also render elsewhere falls back to its own picker behaviour.
 */
export function useCallDevicesContext(): UseCallDevicesResult | null {
  return useContext(CallDevicesContext);
}

/** For surfaces that only ever render inside a call (InCallView, the widget). */
export function useInCallDevices(): UseCallDevicesResult {
  const devices = useContext(CallDevicesContext);
  if (!devices) throw new Error("useInCallDevices must be used inside <InCallDevicesProvider>");
  return devices;
}

interface InCallDevicesProviderProps {
  /** The devices carried over from the pre-join screen (GH-1859). */
  initialSelection?: Partial<DeviceSelection>;
  children: ReactNode;
}

/**
 * Mounts `useCallDevices` once per call, inside `<LiveKitRoom>`, above the
 * InCallView ↔ FloatingCallWidget swap — so the selection, auto-switch and
 * fallback keep running while the call is minimised. Renders the device notice
 * here too, so it shows on any route.
 */
export function InCallDevicesProvider({
  initialSelection,
  children,
}: InCallDevicesProviderProps) {
  const room = useRoomContext();
  const switchDevice = useCallback(
    (kind: CallDeviceKind, deviceId: string) => room.switchActiveDevice(kind, deviceId),
    [room],
  );
  const devices = useCallDevices({ phase: "incall", switchDevice, initialSelection });
  return (
    <CallDevicesContext.Provider value={devices}>
      <CallDeviceNotice devices={devices} />
      {children}
    </CallDevicesContext.Provider>
  );
}
